'use strict'

/* -----------------------------------------------------------------------------
 * dependencies
 * -------------------------------------------------------------------------- */

// 3rd party
import React from 'react'
import { useFormikContext } from 'formik'

// lib
import {
  accessibilityLabel,
  accessibilityRole,
  distribute
} from '../../_support'
import { useField, UseFieldOptions } from '../field'
import { RadioButtonInput } from '../inputs'
import type { RadioButtonInputProps } from '../inputs'
import RadioButtonWrapper from '../inputs/radio-button-input/radio-button-wrapper'
import Box, { BoxProps } from '../../box'
import Pressable from '../../pressable'
import Text, { TextProps } from '../../text'

/* -----------------------------------------------------------------------------
 * RadioGroup
 * -------------------------------------------------------------------------- */

export type RadioGroupProps = BoxProps & {
  children: React.ReactNode
}

export const RadioGroup = ({ children, ...rest }: RadioGroupProps) => {
  return (
    <Box accessibilityRole={accessibilityRole('group')} p='00' m='00' {...rest}>
      {children}
    </Box>
  )
}

/* -----------------------------------------------------------------------------
 * RadioButtonField
 * -------------------------------------------------------------------------- */

export type RadioButtonFieldProps = Omit<BoxProps, 'height' | 'width'> &
  TextProps &
  UseFieldOptions &
  RadioButtonInputProps & {
    size?: number
    label?: string
    value?: string
    onValueChange?: (value?: string) => void
  }

const RadioButtonField = <FormValues extends Record<string, any>>({
  label,
  name,
  disabled,
  value,
  onValueChange,
  size = 20,
  ...rest
}: RadioButtonFieldProps) => {
  const { inputProps, containerProps } = useField({
    name,
    ...rest
  })
  const [boxProps, textProps] = distribute(rest, {
    using: Box.styleKeys,
    to: {},
    rest: {}
  })
  const { values, setFieldTouched, setFieldValue } =
    useFormikContext<FormValues>()
  const isChecked = values[name] === value
  const wrapperProps = (
    isChecked ? { variant: 'violetGradient' } : { bg: 'white' }
  ) as any

  const setNewValue = (newVal?: string) => {
    setFieldTouched(name)
    setFieldValue(name, newVal)
    onValueChange(newVal)
  }

  return (
    <Pressable
      {...containerProps}
      onPress={() => setNewValue(value)}
      disabled={disabled}
    >
      <Box
        flexDirection='row'
        alignItems='center'
        flex='1 1 auto'
        pointerEvents='none'
      >
        <RadioButtonWrapper
          isChecked={isChecked}
          size={size}
          {...wrapperProps}
          {...boxProps}
        >
          <RadioButtonInput
            tintColor='transparent'
            tintColors={{ true: 'transparent', false: 'transparent' }}
            onCheckColor='transparent'
            onFillColor='transparent'
            onTintColor='transparent'
            onAnimationType='fade'
            offAnimationType='fade'
            height={size}
            width={size}
            style={{ background: 'transparent', borderWidth: 0 }}
            accessibilityRole={accessibilityRole('radio')}
            {...accessibilityLabel(`${name} Input`)}
            {...inputProps}
          />
        </RadioButtonWrapper>
        {label ? (
          <Text
            accessibilityRole={accessibilityRole('label')}
            ml='08'
            {...textProps}
          >
            {label}
          </Text>
        ) : null}
      </Box>
    </Pressable>
  )
}

export default RadioButtonField
